/**
 * Barra de ações em massa do Cadastro de Suprimentos (redesign 2026-09-15).
 *
 * Aparece com ≥ 1 selecionado, fixa no rodapé (mesma `BatchBar` da fila de
 * Requisições): "N selecionados", Alterar categoria, Definir mínimo,
 * Vincular fornecedor, Inativar, Excluir e ✕. As três primeiras abrem um
 * modal pequeno (`ModalLote`) que aplica a mudança item a item pelos
 * endpoints que já existem (PATCH do produto / POST do vínculo) — o backend
 * não tem lote para esses campos e o catálogo é pequeno o bastante.
 *
 * No celular a barra encosta nas bordas (16px) e os botões quebram linha;
 * a barra respeita o inset inferior do PWA.
 */
import React, { useEffect, useState } from "react";
import { FiTrash2, FiX } from "react-icons/fi";
import styled from "styled-components";

import { MiniSpinner } from "../../../components/LoadingMarcenaria";
import { useAuth } from "../../../context/AuthContext";
import { listarFornecedores } from "../../../services/compras";
import { CATEGORIAS } from "../helpers";
import { BatchBar } from "../_shared/quadro/styles";
import { Rotulo } from "../RequisicoesCompra/ui";

const Barra = styled(BatchBar)`
  border-radius: 14px;
  animation: barraMassaPopUp .2s ease;
  @keyframes barraMassaPopUp {
    from { transform: translate(-50%, 12px); opacity: 0; }
    to { transform: translate(-50%, 0); opacity: 1; }
  }
  > .contador { font-size: 13px; font-weight: 600; margin-right: 4px; white-space: nowrap; }
  > .contador b { color: var(--accent); }
  > .acao {
    height: 32px; padding: 0 12px; border-radius: 8px; border: none; background: transparent;
    color: var(--ink-2); font: inherit; font-size: 13px; font-weight: 600; cursor: pointer; white-space: nowrap;
    transition: background .12s ease, color .12s ease;
  }
  > .acao:hover:not(:disabled) { background: var(--surface-2); color: var(--ink); }
  > .acao:disabled { opacity: .5; cursor: not-allowed; }
  > .acao.perigo { border: 1px solid var(--danger-border); background: var(--danger-bg); color: var(--danger-text); display: inline-flex; align-items: center; gap: 6px; }
  > .acao.perigo:hover { background: var(--danger-bg); filter: brightness(.97); }
  @media (max-width: 768px) {
    left: 16px; right: 16px; transform: none; bottom: calc(16px + var(--safe-area-bottom, 0px));
    max-width: none; justify-content: center; padding: 10px;
    animation: none;
    > .sep { display: none; }
  }
`;

const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(5, 7, 12, .45); backdrop-filter: blur(2px);
  display: flex; align-items: center; justify-content: center; z-index: 1200; padding: 16px;
`;

const Caixa = styled.div`
  width: min(100%, 440px); background: var(--bg-elevated); border: 1px solid var(--border); border-radius: 14px;
  box-shadow: var(--shadow-4); padding: 20px; display: flex; flex-direction: column; gap: 14px;
  h3 { margin: 0; font-size: 16px; font-weight: 700; }
  p { margin: 0; font-size: 13px; color: var(--ink-3); }
  input, select {
    width: 100%; height: 36px; padding: 0 10px; border-radius: 8px; border: 1px solid var(--border);
    background: var(--surface); color: var(--ink); font: inherit; font-size: 13px; box-sizing: border-box;
  }
  input:focus, select:focus { outline: none; border-color: var(--accent); box-shadow: var(--shadow-focus); }
`;

export const ACOES_LOTE = {
  categoria: { titulo: "Alterar categoria", descricao: "A categoria antiga alimenta BOM e precificação; o tipo de suprimento não muda.", confirmar: "Alterar" },
  minimo: { titulo: "Definir estoque mínimo", descricao: "O mesmo mínimo para todos os selecionados, na unidade de cada item.", confirmar: "Definir" },
  fornecedor: { titulo: "Vincular fornecedor", descricao: "Cria o vínculo produto × fornecedor (sem preço) em cada selecionado que ainda não o tem.", confirmar: "Vincular" },
};

/** Modal de um campo: categoria (select), mínimo (número) ou fornecedor (select). */
export function ModalLote({ acao, quantidade, onConfirmar, onCancelar }) {
  const { request } = useAuth();
  const [valor, setValor] = useState("");
  const [fornecedores, setFornecedores] = useState([]);
  const [executando, setExecutando] = useState(false);

  useEffect(() => {
    if (!acao) return undefined;
    setValor("");
    setExecutando(false);
    const aoTeclar = (e) => { if (e.key === "Escape") onCancelar?.(); };
    document.addEventListener("keydown", aoTeclar);
    return () => document.removeEventListener("keydown", aoTeclar);
  }, [acao]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (acao !== "fornecedor") return;
    listarFornecedores(request, { pageSize: 500, ativo: true })
      .then((p) => setFornecedores(Array.isArray(p) ? p : p?.results || []))
      .catch(() => setFornecedores([]));
  }, [acao, request]);

  if (!acao) return null;
  const meta = ACOES_LOTE[acao];
  const valido = acao === "minimo" ? Number(String(valor).replace(",", ".")) >= 0 && valor !== "" : Boolean(valor);

  async function confirmar() {
    if (!valido) return;
    setExecutando(true);
    try {
      await onConfirmar(acao === "minimo" ? String(valor).replace(",", ".") : valor);
    } finally {
      setExecutando(false);
    }
  }

  return (
    <Overlay onMouseDown={(e) => { if (e.target === e.currentTarget && !executando) onCancelar?.(); }}>
      <Caixa role="dialog" aria-modal="true" aria-labelledby="modal-lote-titulo">
        <h3 id="modal-lote-titulo">{meta.titulo}</h3>
        <p>{quantidade} selecionado{quantidade === 1 ? "" : "s"}. {meta.descricao}</p>
        <div>
          <Rotulo>{acao === "categoria" ? "Categoria" : acao === "minimo" ? "Estoque mínimo" : "Fornecedor"}</Rotulo>
          {acao === "categoria" ? (
            <select value={valor} onChange={(e) => setValor(e.target.value)} autoFocus aria-label="Categoria">
              <option value="">Escolha…</option>
              {CATEGORIAS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          ) : acao === "minimo" ? (
            <input type="number" min="0" step="any" inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value)} autoFocus aria-label="Estoque mínimo" placeholder="0" />
          ) : (
            <select value={valor} onChange={(e) => setValor(e.target.value)} autoFocus aria-label="Fornecedor">
              <option value="">Escolha…</option>
              {fornecedores.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
            </select>
          )}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" className="btn btn--secondary" onClick={onCancelar} disabled={executando}>Voltar</button>
          <button type="button" className="btn btn--primary" onClick={confirmar} disabled={!valido || executando}>
            {executando ? <MiniSpinner size={13} label="Aguarde" /> : null}
            {meta.confirmar}
          </button>
        </div>
      </Caixa>
    </Overlay>
  );
}

export default function BarraMassa({
  quantidade, onAcao, onExcluir, onLimpar, podeEditar, podeDeletar, podeFornecedor, executando = false,
}) {
  if (!quantidade) return null;
  return (
    <Barra role="toolbar" aria-label="Ações em massa">
      <span className="contador"><b>{quantidade}</b> selecionado{quantidade === 1 ? "" : "s"}</span>
      <span className="sep" />
      {podeEditar ? <button type="button" className="acao" disabled={executando} onClick={() => onAcao("categoria")}>Alterar categoria</button> : null}
      {podeEditar ? <button type="button" className="acao" disabled={executando} onClick={() => onAcao("minimo")}>Definir mínimo</button> : null}
      {podeFornecedor ? <button type="button" className="acao" disabled={executando} onClick={() => onAcao("fornecedor")}>Vincular fornecedor</button> : null}
      {podeEditar ? <button type="button" className="acao" disabled={executando} onClick={() => onAcao("inativar")}>Inativar</button> : null}
      {podeDeletar ? (
        <button type="button" className="acao perigo" disabled={executando} onClick={onExcluir}>
          <FiTrash2 size={12} /> Excluir
        </button>
      ) : null}
      <button type="button" className="acao" onClick={onLimpar} aria-label="Limpar seleção" title="Limpar seleção" style={{ width: 32, padding: 0 }}>
        <FiX size={14} />
      </button>
    </Barra>
  );
}
